import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '@/contexts/ProductsContext';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/hooks/useAuth';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import { useWishlist } from '@/hooks/useWishlist';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { submitReview } from '@/lib/productReviewsApi';
import { ProductHostContext, type ProductHost } from '@/shared/rxk-product/host';

// rxk's side of the shared product page (src/shared/rxk-product): its own
// links (with the /ko or /bn prefix), catalog, toast, account, wishlist and
// review form, and its checkout page.
export function RxkProductHost({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { products, routineSet } = useProducts();
  const { showToast } = useToast();
  const { currentUser } = useAuth();
  const { isPreview } = useSiteDesign();
  const wishlist = useWishlist();

  const host = useMemo<ProductHost>(
    () => ({
      links: {
        home: prefix + '/',
        products: prefix + '/products',
        product: (p) => `${prefix}/products/${p.id}`,
        set: prefix + '/3-step-treatment',
        login: prefix + '/login',
      },
      products,
      routineSet,
      toast: (m) => showToast(m),
      currentUser: currentUser ? { id: currentUser.id, name: currentUser.name } : null,
      isPreview,
      wishlist,
      submitReview,
      checkout: () => navigate(prefix + '/checkout'),
    }),
    [prefix, products, routineSet, showToast, currentUser, isPreview, wishlist, navigate],
  );

  return <ProductHostContext.Provider value={host}>{children}</ProductHostContext.Provider>;
}
